import React, { useState } from "react";
import { Link } from "react-router-dom"; // Add this import for navigation

function Contact() {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  
  const [annotationSubmitted, setAnnotationSubmitted] = useState(false);
  const [annotationLoading, setAnnotationLoading] = useState(false);
  const [annotationError, setAnnotationError] = useState('');
  
  const [activeTab, setActiveTab] = useState('feedback'); // 'feedback' or 'annotation'
  const [submissionType, setSubmissionType] = useState('whole'); // 'whole' or 'single'
  
  // Annotation form state for whole plasmid
  const [wholePlasmidData, setWholePlasmidData] = useState({
    name: '',
    email: '',
    plasmidName: '',
    genbankId: '',
    pubmedId: '',
    validationMethod: 'Computational',
    incType: '',
    mobType: '',
    ptu: '',
    isolationSource: '',
    notes: ''
  });

  // Annotation form state for single feature
  const [singleFeatureData, setSingleFeatureData] = useState({
    name: '',
    email: '',
    plasmidName: '',
    featureName: '',
    featureType: 'CDS',
    validationMethod: 'Computational',
    pubmedId: '',
    notes: ''
  });
  
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // IMPORTANT: Replace with your Google Apps Script URL
  const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyvxPffSJwzEUwVoheD7CNFZknDqlYnHX9XPIOtMzu05mBlKqeXwZ8SWkDZrspYcxXfdw/exec';

  // Feedback form handler (existing)
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setFeedbackLoading(true);

    const formData = new FormData(e.target);
    
    try {
      const response = await fetch('https://formsubmit.co/plasanndevs@gmail.com', {
        method: 'POST',
        body: formData
      });
      
      setFeedbackSubmitted(true);
      e.target.reset();
      
      setTimeout(() => {
        setFeedbackSubmitted(false);
      }, 5000);
      
    } catch (error) {
      alert('Failed to send. Please try again.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Whole plasmid input change handler
  const handleWholePlasmidChange = (e) => {
    const { name, value } = e.target;
    setWholePlasmidData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Single feature input change handler
  const handleSingleFeatureChange = (e) => {
    const { name, value } = e.target;
    setSingleFeatureData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Different file types based on submission type
      const validTypes = submissionType === 'whole' 
        ? ['.gb', '.gbk', '.csv', '.tsv', '.gff', '.gff3']
        : ['.gb', '.gbk', '.fasta', '.fa'];
      
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
      
      if (!validTypes.includes(fileExtension)) {
        const acceptedFormats = submissionType === 'whole'
          ? '.gb, .gbk, .csv, .tsv, .gff'
          : '.gb, .gbk, .fasta, .fa';
        setAnnotationError(`Please upload a valid file type: ${acceptedFormats}`);
        return;
      }
      
      if (selectedFile.size > 10 * 1024 * 1024) {
        setAnnotationError('File size must be less than 10MB');
        return;
      }
      
      setFile(selectedFile);
      setAnnotationError('');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const event = { target: { files: [e.dataTransfer.files[0]] } };
      handleFileChange(event);
    }
  };

  const handleAnnotationSubmit = async (e) => {
    e.preventDefault();
    
    // Validation based on submission type
    if (submissionType === 'whole') {
      if (!wholePlasmidData.name || !wholePlasmidData.email || !wholePlasmidData.plasmidName) {
        setAnnotationError('Please fill in all required fields (marked with *)');
        return;
      }
    } else {
      if (!singleFeatureData.name || !singleFeatureData.email || !singleFeatureData.featureName) {
        setAnnotationError('Please fill in all required fields (marked with *)');
        return;
      }
    }

    setAnnotationLoading(true);
    setAnnotationError('');

    try {
      let fileData = null;
      if (file) {
        fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve({
              name: file.name,
              mimeType: file.type,
              data: base64
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      const payload = {
        submissionType: submissionType,
        ...(submissionType === 'whole' ? wholePlasmidData : singleFeatureData),
        file: fileData,
        timestamp: new Date().toISOString()
      };

      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      setAnnotationSubmitted(true);
      
      // Reset form data
      if (submissionType === 'whole') {
        setWholePlasmidData({
          name: '',
          email: '',
          plasmidName: '',
          genbankId: '',
          pubmedId: '',
          validationMethod: 'Computational',
          incType: '',
          mobType: '',
          ptu: '',
          isolationSource: '',
          notes: ''
        });
      } else {
        setSingleFeatureData({
          name: '',
          email: '',
          plasmidName: '',
          featureName: '',
          featureType: 'CDS',
          validationMethod: 'Computational',
          pubmedId: '',
          notes: ''
        });
      }
      setFile(null);
      
      setTimeout(() => {
        setAnnotationSubmitted(false);
      }, 5000);

    } catch (err) {
      console.error('Submission error:', err);
      setAnnotationError('Failed to submit. Please try again or contact us directly.');
    } finally {
      setAnnotationLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div 
        className="text-white p-6 rounded-lg mb-6 shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #354931 0%, #5d8156 50%, #86bb7c 100%)'
        }}
      >
        <h1 className="text-3xl font-bold mb-2">📧 Contact Us</h1>
        <p style={{ color: '#c8d3c5' }}>
          Get in touch with the PlasAnn development team
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Left Column - Contact Information */}
        <div className="space-y-6">
          
          {/* Contact Information */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">
              Contact Information
            </h2>
            <div className="space-y-6">
              {/* Lead Developer */}
              <div>
                <p className="font-medium text-gray-700">Habibul Islam</p>
                <p className="text-[#6b7966]">Lead Developer</p>
                <p className="text-sm text-gray-600">Graduate Student</p>
                <p className="text-sm text-gray-600">Lopatkin Lab</p>
                <p className="text-sm text-gray-600">Department of Chemical Engineering</p>
                <p className="text-sm text-gray-600">University of Rochester</p>
                <a href="mailto:hislam2@ur.rochester.edu" className="text-[#4e5a4c] hover:underline">
                  hislam2@ur.rochester.edu
                </a>
              </div>
              
              {/* Principal Investigator */}
              <div>
                <p className="font-medium text-gray-700">Allison Lopatkin, PhD</p>
                <p className="text-[#6b7966]">Principal Investigator</p>
                <p className="text-sm text-gray-600">Assistant Professor</p>
                <p className="text-sm text-gray-600">Primary | Department of Chemical Engineering</p>
                <p className="text-sm text-gray-600">Secondary | Department of Microbiology & Immunology</p>
                <p className="text-sm text-gray-600">Secondary | Department of Biomedical Engineering</p>
                <p className="text-sm text-gray-600 mt-2">Office: Wegmans Hall 4303</p>
                <p className="text-sm text-gray-600">Lab: Gavett Hall 313</p>
                <a href="mailto:allison.lopatkin@rochester.edu" className="text-[#4e5a4c] hover:underline">
                  allison.lopatkin@rochester.edu
                </a>
                <a href="https://lopatkinlab.com" className="text-[#4e5a4c] hover:underline block mt-1">
                  www.lopatkinlab.com
                </a>
              </div>
            </div>
          </div>

          {/* Citation */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c] flex items-center">
              📄 How to Cite PlasAnn
            </h2>
            <div className="bg-[#f8faf8] p-4 rounded-lg border-l-4 border-[#6b7966]">
              <p className="text-sm text-gray-700 font-mono leading-relaxed">
                Islam, Habibul, Abhishek Sharma, Jordan Blair, and Allison J. Lopatkin. "PlasAnn: a curated plasmid-specific database and annotation pipeline for standardized gene and function analysis." <em>Nucleic Acids Research</em> 54, no. 3 (2026): gkaf1507.
              </p>
            </div>
            <div className="mt-3 flex space-x-2">
              <button 
                onClick={() => {
                  const bibtex = `@article{10.1093/nar/gkaf1507,
    author = {Islam, Habibul and Sharma, Abhishek and Blair, Jordan and Lopatkin, Allison J},
    title = {PlasAnn: a curated plasmid-specific database and annotation pipeline for standardized gene and function analysis},
    journal = {Nucleic Acids Research},
    volume = {54},
    number = {3},
    pages = {gkaf1507},
    year = {2026},
    month = {01},
    abstract = {Conjugative plasmids are key drivers of bacterial adaptation, enabling the horizontal transfer of accessory genes within and across diverse microbial populations, yet annotating them remains challenging due to their highly mosaic genetic architectures and inconsistent gene naming conventions that complicate functional predictions and comparative analyses. To address this, we developed PlasAnn, a database designed specifically for genes encoded on natural plasmids, paired with a dedicated annotation pipeline (available via Bioconda or through the URL https://plasann.rochester.edu/). The curated database provides highly accurate, plasmid-type-specific gene names with standardized functional annotations, enabling direct comparison across plasmids without manual curation or specialized expertise, while the integrated annotation tool incorporates other common plasmid features for a fast, one-stop solution that outperforms broad prokaryotic genome annotation pipelines in both accuracy and efficiency. We demonstrate PlasAnn's utility by showing that plasmid accessory genes from different groups often share conserved repertoires, suggesting dynamic, modular networks of interconnected genes, and by revealing that plasmid-encoded transposable elements frequently carry genes related to bacterial adaptation beyond antibiotic resistance, including metabolism, virulence, and stress responses, emphasizing their broader contributions to fitness and adaptability. These insights, not captured by current field-standard tools, highlight how PlasAnn improves plasmid annotation and advances our understanding of plasmid biology, microbial ecology, and evolution.},
    issn = {1362-4962},
    doi = {10.1093/nar/gkaf1507},
    url = {https://doi.org/10.1093/nar/gkaf1507},
    eprint = {https://academic.oup.com/nar/article-pdf/54/3/gkaf1507/66590375/gkaf1507.pdf},
}`;
                  
                  // Create a downloadable .bib file
                  const blob = new Blob([bibtex], { type: 'text/plain' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'plasann.bib';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                }}
                className="flex items-center space-x-1 bg-[#6b7966] text-white px-3 py-1 rounded text-sm hover:bg-[#5d6a5b] cursor-pointer"
              >
                📄 BibTeX
              </button>
              <a 
                href="https://doi.org/10.1093/nar/gkaf1507" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-1 bg-[#6b7966] text-white px-3 py-1 rounded text-sm hover:bg-[#5d6a5b]"
              >
                🔗 DOI
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c] flex items-center">
              🔗 Resources
            </h2>
            <div className="space-y-2">
              <a href="https://github.com/ajlopatkin/PlasAnn" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-[#6b7966] hover:text-[#4e5a4c] hover:underline">
                <span>💻</span>
                <span>GitHub Repository</span>
              </a>
              <a href="https://lopatkinlab.com/resources.html" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-[#6b7966] hover:text-[#4e5a4c] hover:underline">
                <span>🧪</span>
                <span>Lopatkin Lab Resources</span>
              </a>
            </div>
          </div>
        </div>

        {/* Right Column - Contact Forms */}
        <div className="space-y-6">
          
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md border border-[#d1ddd1] overflow-hidden">
            <div className="flex border-b border-[#d1ddd1]">
              <button
                onClick={() => setActiveTab('feedback')}
                className={`flex-1 py-3 px-4 font-medium transition-colors ${
                  activeTab === 'feedback'
                    ? 'bg-[#6b7966] text-white'
                    : 'bg-[#f8faf8] text-gray-600 hover:bg-[#f1f4f1]'
                }`}
              >
                💬 Send Feedback
              </button>
              <button
                onClick={() => setActiveTab('annotation')}
                className={`flex-1 py-3 px-4 font-medium transition-colors ${
                  activeTab === 'annotation'
                    ? 'bg-[#6b7966] text-white'
                    : 'bg-[#f8faf8] text-gray-600 hover:bg-[#f1f4f1]'
                }`}
              >
                📝 Submit Annotation
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'feedback' ? (
                // Feedback Form
                feedbackSubmitted ? (
                  <div className="bg-green-50 border-l-4 border-green-500 p-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">✅</span>
                      <div>
                        <p className="font-semibold text-green-800">Message Sent Successfully!</p>
                        <p className="text-sm text-green-700 mt-1">
                          Thank you for your feedback. We'll get back to you soon.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                      <input
                        type="text"
                        name="subject"
                        required
                        className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                        placeholder="Tell us what you think..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={feedbackLoading}
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                        feedbackLoading 
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                          : 'bg-[#6b7966] text-white hover:bg-[#5d6a5b]'
                      }`}
                    >
                      {feedbackLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>📤</span>
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </form>
                )
              ) : (
                // Annotation Form
                annotationSubmitted ? (
                  <div className="bg-green-50 border-l-4 border-green-500 p-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">✅</span>
                      <div>
                        <p className="font-semibold text-green-800">Annotation Submitted Successfully!</p>
                        <p className="text-sm text-green-700 mt-1">
                          Thank you for your contribution. We'll review it soon.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleAnnotationSubmit} className="space-y-4">
                    
                    {/* Submission Type Selection */}
                    <div className="bg-[#f8faf8] border border-[#d1ddd1] rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Submission Type *</label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="submissionType"
                            value="whole"
                            checked={submissionType === 'whole'}
                            onChange={(e) => {
                              setSubmissionType(e.target.value);
                              setFile(null);
                              setAnnotationError('');
                            }}
                            className="w-4 h-4 text-[#6b7966] focus:ring-[#6b7966]"
                          />
                          <span className="text-sm font-medium text-gray-700">🧬 Whole Annotated Plasmid</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="submissionType"
                            value="single"
                            checked={submissionType === 'single'}
                            onChange={(e) => {
                              setSubmissionType(e.target.value);
                              setFile(null);
                              setAnnotationError('');
                            }}
                            className="w-4 h-4 text-[#6b7966] focus:ring-[#6b7966]"
                          />
                          <span className="text-sm font-medium text-gray-700">🔍 Single Annotated Feature</span>
                        </label>
                      </div>
                    </div>

                    {/* Conditional Form Fields */}
                    {submissionType === 'whole' ? (
                      // WHOLE PLASMID FORM
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                            <input
                              type="text"
                              name="name"
                              required
                              value={wholePlasmidData.name}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                              type="email"
                              name="email"
                              required
                              value={wholePlasmidData.email}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Plasmid Name *</label>
                          <input
                            type="text"
                            name="plasmidName"
                            required
                            value={wholePlasmidData.plasmidName}
                            onChange={handleWholePlasmidChange}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            placeholder="e.g., pUC19"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">GenBank ID</label>
                            <input
                              type="text"
                              name="genbankId"
                              value={wholePlasmidData.genbankId}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                              placeholder="e.g., NC_000001"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">PubMed ID</label>
                            <input
                              type="text"
                              name="pubmedId"
                              value={wholePlasmidData.pubmedId}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                              placeholder="e.g., 12345678"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Validation Method</label>
                          <select
                            name="validationMethod"
                            value={wholePlasmidData.validationMethod}
                            onChange={handleWholePlasmidChange}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                          >
                            <option value="Experimental">Experimental</option>
                            <option value="Computational">Computational</option>
                            <option value="Literature">Literature</option>
                            <option value="Published">Published</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Inc Type</label>
                            <input
                              type="text"
                              name="incType"
                              value={wholePlasmidData.incType}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                              placeholder="e.g., IncFII"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Mob Type</label>
                            <input
                              type="text"
                              name="mobType"
                              value={wholePlasmidData.mobType}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                              placeholder="e.g., MOB_F"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">PTU</label>
                            <input
                              type="text"
                              name="ptu"
                              value={wholePlasmidData.ptu}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                              placeholder="Plasmid Taxonomic Units"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Isolation Source</label>
                            <input
                              type="text"
                              name="isolationSource"
                              value={wholePlasmidData.isolationSource}
                              onChange={handleWholePlasmidChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                              placeholder="e.g., E. coli clinical isolate"
                            />
                          </div>
                        </div>

                        {/* File Upload for Whole Plasmid */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Upload Annotated File</label>
                          <div
                            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                              dragActive ? 'border-[#6b7966] bg-[#f8faf8]' : 'border-[#d1ddd1]'
                            }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                          >
                            <input
                              type="file"
                              id="file-upload-whole"
                              className="hidden"
                              onChange={handleFileChange}
                              accept=".gb,.gbk,.csv,.tsv,.gff,.gff3"
                            />
                            
                            {file ? (
                              <div className="space-y-1">
                                <div className="text-2xl">📄</div>
                                <p className="text-sm font-medium text-gray-700">{file.name}</p>
                                <button
                                  type="button"
                                  onClick={() => setFile(null)}
                                  className="text-red-600 text-xs hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                            ) : (
                              <div>
                                <div className="text-2xl mb-1">📤</div>
                                <label htmlFor="file-upload-whole" className="text-[#6b7966] text-sm cursor-pointer hover:underline">
                                  Choose file
                                </label>
                                <p className="text-xs text-gray-500 mt-1">.gb, .gbk, .csv, .tsv, .gff (max 10MB)</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                          <textarea
                            name="notes"
                            value={wholePlasmidData.notes}
                            onChange={handleWholePlasmidChange}
                            rows={3}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            placeholder="Additional information about your submission..."
                          />
                        </div>
                      </>
                    ) : (
                      // SINGLE FEATURE FORM
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                            <input
                              type="text"
                              name="name"
                              required
                              value={singleFeatureData.name}
                              onChange={handleSingleFeatureChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                              type="email"
                              name="email"
                              required
                              value={singleFeatureData.email}
                              onChange={handleSingleFeatureChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Plasmid Name</label>
                          <input
                            type="text"
                            name="plasmidName"
                            value={singleFeatureData.plasmidName}
                            onChange={handleSingleFeatureChange}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            placeholder="e.g., pUC19 (optional)"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Feature Name *</label>
                          <input
                            type="text"
                            name="featureName"
                            required
                            value={singleFeatureData.featureName}
                            onChange={handleSingleFeatureChange}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            placeholder="e.g., ampR, lacZ promoter"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Feature Type</label>
                            <select
                              name="featureType"
                              value={singleFeatureData.featureType}
                              onChange={handleSingleFeatureChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            >
                              <option value="CDS">CDS (Coding Sequence)</option>
                              <option value="Promoter">Promoter</option>
                              <option value="Terminator">Terminator</option>
                              <option value="Origin">Origin of Replication</option>
                              <option value="Resistance">Resistance Marker</option>
                              <option value="RBS">RBS (Ribosome Binding Site)</option>
                              <option value="Enhancer">Enhancer</option>
                              <option value="Operator">Operator</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Validation Method</label>
                            <select
                              name="validationMethod"
                              value={singleFeatureData.validationMethod}
                              onChange={handleSingleFeatureChange}
                              className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            >
                              <option value="Experimental">Experimental</option>
                              <option value="Computational">Computational</option>
                              <option value="Literature">Literature</option>
                              <option value="Published">Published</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">PubMed ID</label>
                          <input
                            type="text"
                            name="pubmedId"
                            value={singleFeatureData.pubmedId}
                            onChange={handleSingleFeatureChange}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            placeholder="e.g., 12345678"
                          />
                        </div>

                        {/* File Upload for Single Feature */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Upload Feature File</label>
                          <div
                            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                              dragActive ? 'border-[#6b7966] bg-[#f8faf8]' : 'border-[#d1ddd1]'
                            }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                          >
                            <input
                              type="file"
                              id="file-upload-single"
                              className="hidden"
                              onChange={handleFileChange}
                              accept=".gb,.gbk,.fasta,.fa"
                            />
                            
                            {file ? (
                              <div className="space-y-1">
                                <div className="text-2xl">📄</div>
                                <p className="text-sm font-medium text-gray-700">{file.name}</p>
                                <button
                                  type="button"
                                  onClick={() => setFile(null)}
                                  className="text-red-600 text-xs hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                            ) : (
                              <div>
                                <div className="text-2xl mb-1">📤</div>
                                <label htmlFor="file-upload-single" className="text-[#6b7966] text-sm cursor-pointer hover:underline">
                                  Choose file
                                </label>
                                <p className="text-xs text-gray-500 mt-1">.gb, .gbk, .fasta, .fa (max 10MB)</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                          <textarea
                            name="notes"
                            value={singleFeatureData.notes}
                            onChange={handleSingleFeatureChange}
                            rows={3}
                            className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966]"
                            placeholder="Additional information about the feature..."
                          />
                        </div>
                      </>
                    )}

                    {annotationError && (
                      <div className="bg-red-50 border-l-4 border-red-500 p-3">
                        <p className="text-sm text-red-700">{annotationError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={annotationLoading}
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                        annotationLoading 
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                          : 'bg-[#6b7966] text-white hover:bg-[#5d6a5b]'
                      }`}
                    >
                      {annotationLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span>📤</span>
                          <span>Submit Annotation</span>
                        </>
                      )}
                    </button>
                  </form>
                )
              )}
            </div>
          </div>

          {/* Response Time */}
          <div className="bg-[#f1f4f1] border border-[#d1ddd1] rounded-lg p-4">
            <h3 className="font-semibold text-[#4e5a4c] mb-2">📅 Response Time</h3>
            <p className="text-sm text-gray-600">
              We typically respond to inquiries within 2-3 business days. For urgent technical issues, 
              please include "URGENT" in your subject line.
            </p>
          </div>

          {/* FAQ Link */}
          <div className="bg-[#e8efe8] border border-[#d1ddd1] rounded-lg p-4">
            <h3 className="font-semibold text-[#4e5a4c] mb-2">❓ Before You Contact Us</h3>
            <p className="text-sm text-gray-600 mb-2">
              Please check our FAQ and documentation first - your question might already be answered!
            </p>
            <Link 
              to="/faq" 
              className="text-[#6b7966] hover:text-[#4e5a4c] text-sm font-medium hover:underline inline-block"
            >
              View FAQ & Documentation →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;